import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Box, createTheme } from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { isNewFttsImplementation } from '../../common/functions/ftts_new_implementation.function';
import { isNewIpranImplementation } from '../../common/functions/ipran_new_implementation.function';
import {
  isNewOtnImplementation,
  isOtnEndpointAlarm,
} from '../../common/functions/otn_new_implementation.function';
import { OtnEndpointsTableCell } from '../ActiveAlarmsCard/otnEndpointUi';
import '../ActiveAlarmsCard/otnEndpointStyles.css';
import TableChartIcon from '@mui/icons-material/TableChart';
import MapIcon from '@mui/icons-material/Map';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import Tooltip from '@mui/material/Tooltip';
import TuneIcon from '@mui/icons-material/Tune';
import { downloadExcelSheetFunction } from '../../common/functions/download_excel_sheet..function';
import { ExpandableTable } from './expandableTable';
import {
  borderRadius,
  boxSizing,
  color,
  display,
  fontSize,
  fontWeight,
  lineHeight,
  padding,
} from '@mui/system';
import DateRangePickerComponent from '../../common/components/Date_Range_Picker.component';
import apiClient from '../../common/functions/api_axios_instance.function';
import { handleApiError } from '../../common/functions/errorHandler.function';

const theme = createTheme({
  typography: {
    // fontFamily: 'Poppins',
  },
});

export default function ActiveAlarmsTable({
  setViewType,
  data,
  viewType,
  selectedDate,
  setSelectedDate,
  state,
  setVideoPlayTime,
  setVideoPlayTimeEnd,
  alramType,
  open,
  setAlramType,
  toggleDrawer,
  applyFilters,
  filtersValue,
}) {
  const [position, setPosition] = useState(0);
  const [historyAlarmsData, setHistoryAlarmsData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const isFetchingRef = useRef(false);
  const debounceTimeoutRef = useRef(null);

  // Use live alarms data for live mode, history alarms data for history mode
  const validData =
    alramType === 'live'
      ? Array?.isArray(data)
        ? data
        : []
      : Array?.isArray(historyAlarmsData)
        ? historyAlarmsData
        : [];

  useEffect(() => {
    // Add null checks to prevent runtime errors
    if (!selectedDate || !Array.isArray(selectedDate) || selectedDate.length !== 2) {
      console.warn('Invalid selectedDate format:', selectedDate);
      return;
    }

    const [startDate, endDate] = selectedDate;

    // Check if both dates are valid dayjs objects
    if (!startDate || !endDate || !startDate.isValid || !endDate.isValid) {
      console.warn('Invalid dates in selectedDate:', { startDate, endDate });
      return;
    }

    // Check if both dates are the same (single day selection)
    if (startDate.isSame(endDate, 'day')) {
      const isCurrentDate = startDate.isSame(dayjs(), 'day');

      if (isCurrentDate && alramType === 'live') {
        // For live alarms on current date, use 24 hours ago to current time
        const now = dayjs();
        const twentyFourHoursAgo = now.subtract(24, 'hour');
        setVideoPlayTime(
          `${twentyFourHoursAgo.format('YYYY-MM-DD')} ${twentyFourHoursAgo.format('HH:mm:ss')}`
        );
        setVideoPlayTimeEnd(`${now.format('YYYY-MM-DD')} ${now.format('HH:mm:ss')}`);
      } else {
        // For history alarms or past dates, use the selected date
        setVideoPlayTime(`${startDate.format('YYYY-MM-DD')} 00:00:00`);
        setVideoPlayTimeEnd(`${startDate.format('YYYY-MM-DD')} 23:59:59`);
      }
    } else {
      // Date range selection - use the actual start and end dates
      setVideoPlayTime(`${startDate.format('YYYY-MM-DD')} 00:00:00`);
      setVideoPlayTimeEnd(`${endDate.format('YYYY-MM-DD')} 23:59:59`);
    }
    setPosition(0);
  }, [selectedDate, setVideoPlayTime, setVideoPlayTimeEnd]);

  const handleChange = (value) => {
    setAlramType(value);
  };

  const handleDateChange = (newDate) => {
    try {
      // Validate the new date before setting it
      if (newDate && Array.isArray(newDate) && newDate.length === 2) {
        const [startDate, endDate] = newDate;

        // Ensure both dates are valid dayjs objects
        if (startDate && endDate && startDate.isValid && endDate.isValid) {
          setSelectedDate(newDate);
        } else {
          console.warn('Invalid dates received in handleDateChange:', { startDate, endDate });
        }
      } else {
        console.warn('Invalid date format received in handleDateChange:', newDate);
      }
    } catch (error) {
      console.error('Error in handleDateChange:', error);
    }
  };

  // Function to fetch history alarms data
  const fetchHistoryAlarmsData = useCallback(async () => {
    if (alramType !== 'history') {
      console.log('Not fetching history data - alarm type is not history:', alramType);
      return;
    }

    // Prevent concurrent calls
    if (isFetchingRef.current) {
      console.log('Already fetching history data, skipping...');
      return;
    }

    // Validate selectedDate before proceeding
    if (!selectedDate || !Array.isArray(selectedDate) || selectedDate.length !== 2) {
      console.warn('Cannot fetch history data - invalid selectedDate:', selectedDate);
      return;
    }

    const [startDate, endDate] = selectedDate;
    if (!startDate || !endDate || !startDate.isValid || !endDate.isValid) {
      console.warn('Cannot fetch history data - invalid dates:', { startDate, endDate });
      return;
    }

    try {
      console.log('Fetching history alarms data...');
      isFetchingRef.current = true;
      setHistoryLoading(true);
      const selectedTab = sessionStorage.getItem('selectedTab');

      // Get the date range from selectedDate
      const dateTime = `${startDate.format('YYYY-MM-DD')} 00:00:00`;
      const endDateTime = `${endDate.format('YYYY-MM-DD')} 23:59:59`;

      console.log('API call parameters:', {
        selectedTab,
        dateTime,
        endDateTime,
        filtersValue,
      });

      const response = await apiClient.get(
        selectedTab === 'ftts'
          ? isNewFttsImplementation()
            ? `/snmpFTTS/historicAlarms`
            : `/ftts/historicAlarms`
          : selectedTab === 'mpbn'
            ? `/mpbn/historicAlarms`
            : selectedTab === 'otn'
              ? isNewOtnImplementation()
                ? `/snmpOTN/historicAlarms`
                : `/otn/historicAlarms`
              : selectedTab === 'ipran'
                ? isNewIpranImplementation()
                  ? `/snmpIPRAN/historicAlarms`
                  : `/ipran/historicAlarms`
                : '/snmpFTTS/historicAlarms',
        {
          params: {
            region: filtersValue?.region,
            city: filtersValue?.city,
            ...(selectedTab !== 'otn' && { ring: filtersValue?.ring }),
            dateTime: dateTime,
            endDateTime: endDateTime,
          },
        }
      );

      const responseData =
        selectedTab === 'ftts' || selectedTab === 'mpbn'
          ? response?.data
          : response?.data?.response || [];

      console.log('History alarms API response:', responseData);
      console.log('First item structure:', responseData?.[0]);
      console.log('Data length:', responseData?.length);
      setHistoryAlarmsData(responseData);
    } catch (error) {
      handleApiError(error);
      setHistoryAlarmsData([]);
    } finally {
      isFetchingRef.current = false;
      setHistoryLoading(false);
    }
  }, [alramType, selectedDate, filtersValue]);

  // Consolidated useEffect to handle all history alarms data fetching
  useEffect(() => {
    if (alramType === 'history') {
      // Only fetch if we have valid date range
      if (selectedDate && Array.isArray(selectedDate) && selectedDate.length === 2) {
        // Clear any existing timeout
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        // Debounce the API call to prevent rapid successive calls
        debounceTimeoutRef.current = setTimeout(() => {
          console.log('Triggering history alarms fetch due to:', {
            alramType,
            selectedDate,
            filtersValue,
          });
          fetchHistoryAlarmsData();
        }, 300); // 300ms debounce
      }
    } else {
      // Clear history data when switching to live mode
      setHistoryAlarmsData([]);
      // Clear any pending timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    }

    // Cleanup function to clear timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [alramType, selectedDate, filtersValue, fetchHistoryAlarmsData]);

  const columns = useMemo(() => {
    console.log('Generating columns with validData:', validData);
    const isFttsTab = sessionStorage.getItem('selectedTab') === 'ftts';
    const isIpranTab = sessionStorage.getItem('selectedTab') === 'ipran';
    const isOtnTab = sessionStorage.getItem('selectedTab') === 'otn';
    const isNewFttsImplementationFlag = isNewFttsImplementation();
    const isNewIpranImplementationFlag = isNewIpranImplementation();
    const isNewOtnImplementationFlag = isNewOtnImplementation();
    const isOtnEndpointMode =
      isOtnTab && isNewOtnImplementationFlag && validData?.some(isOtnEndpointAlarm);

    if (isOtnEndpointMode) {
      return [
        {
          accessorKey: 'layer_name',
          header: 'Layer',
          size: 160,
          enableSorting: true,
          enableColumnFilter: true,
          Cell: ({ row }) => (
            <div className="otn-layer-cell">
              <span className="otn-layer-cell__name">{row.original.layer_name}</span>
              <span className="otn-layer-cell__meta">
                {[row.original.region, row.original.source_city].filter(Boolean).join(' · ')}
              </span>
            </div>
          ),
        },
        {
          accessorKey: 'commissioned_at',
          header: 'Commission Date',
          size: 120,
          enableSorting: true,
          enableColumnFilter: true,
          Cell: ({ cell }) => {
            const value = cell.getValue();
            if (!value) {
              return '--';
            }
            const date = new Date(value);
            return isNaN(date.getTime()) ? '--' : date.toLocaleDateString('en-GB');
          },
        },
        {
          accessorKey: 'length',
          header: 'Length',
          size: 90,
          enableSorting: true,
          enableColumnFilter: true,
          Cell: ({ cell }) => {
            const value = cell.getValue();
            return value !== null && value !== undefined && value !== ''
              ? `${parseFloat(value).toFixed(2)} km`
              : '--';
          },
        },
        {
          id: 'endpoints',
          header: 'Endpoints',
          size: 300,
          enableSorting: false,
          enableColumnFilter: false,
          Cell: ({ row }) => <OtnEndpointsTableCell alarm={row.original} alarmType={alramType} />,
        },
      ];
    }

    const keysToRemove = [
      'lat',
      'long',
      'xmin',
      'xmax',
      'ymin',
      'ymax',
      'commissioned_at',
      'length',
      'alarm_source',
      'first_occurred',
      'last_occurred',
      'geojson', // New MPBN field to exclude
      'source_trunk', // Internal field
      'color', // Color is used for styling, not display
    ];

    // For FTTS with new implementation, remove alarm_inactive_ts and alarm_active_ts from table
    // For FTTS with old implementation, remove layer_name, alarm_active_ts, and alarm_inactive_ts from table
    if (isFttsTab) {
      if (isNewFttsImplementationFlag) {
        keysToRemove.push('alarm_inactive_ts');
        keysToRemove.push('alarm_active_ts');
      } else {
        keysToRemove.push('layer_name');
        keysToRemove.push('alarm_active_ts');
        keysToRemove.push('alarm_inactive_ts');
      }
    }

    // For IPRAN with new implementation, remove alarm_inactive_ts and alarm_active_ts from table
    // For IPRAN with old implementation, remove layer_name, alarm_active_ts, and alarm_inactive_ts from table
    if (isIpranTab) {
      if (isNewIpranImplementationFlag) {
        keysToRemove.push('alarm_inactive_ts');
        keysToRemove.push('alarm_active_ts');
      } else {
        keysToRemove.push('layer_name');
        keysToRemove.push('alarm_active_ts');
        keysToRemove.push('alarm_inactive_ts');
      }
    }

    // For OTN with new implementation, remove alarm_inactive_ts and alarm_active_ts from table
    // For OTN with old implementation, remove layer_name, alarm_active_ts, and alarm_inactive_ts from table
    if (isOtnTab) {
      if (isNewOtnImplementationFlag) {
        keysToRemove.push('alarm_inactive_ts');
        keysToRemove.push('alarm_active_ts');
      } else {
        keysToRemove.push('layer_name');
        keysToRemove.push('alarm_active_ts');
        keysToRemove.push('alarm_inactive_ts');
      }
    }

    // Get all unique keys from the data objects except the ones to remove
    const keys = validData?.reduce((acc, obj) => {
      Object.keys(obj).forEach((key) => {
        if (!keysToRemove.includes(key) && !acc.includes(key)) {
          acc.push(key);
        }
      });
      return acc;
    }, []);

    // Create Layer Name column first if it exists in the data
    const hasLayerName = validData?.some((obj) => obj.layer_name);
    const layerNameColumn = hasLayerName
      ? [
          {
            accessorKey: 'layer_name',
            header: 'Layer Name',
            size: 150,
            enableSorting: true,
            enableColumnFilter: true,
            enablePinning: true,
            enableColumnActions: true,
            Cell: ({ cell }) => cell.getValue(),
          },
        ]
      : [];

    const columnsWithIndex = keys?.map((key) => {
      // Custom header mapping for FTTS with new implementation
      let header = '';
      if (
        (isFttsTab && isNewFttsImplementationFlag) ||
        (isIpranTab && isNewIpranImplementationFlag) ||
        (isOtnTab && isNewOtnImplementationFlag)
      ) {
        switch (key) {
          case 'source_lookup_id':
            header = 'Alarm lookup id';
            break;
          case 'alarm_active_ts':
            header = 'Alarm Active Time';
            break;
          case 'clearance_status':
            header = 'Clearance Status';
            break;
          case 'acknowledgement_status':
            header = 'Acknowledgement Status';
            break;
          default:
            header =
              key === 'acknowledgement_status' || key === 'clearance_status'
                ? ''
                : key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
        }
      } else {
        header =
          key === 'acknowledgement_status' || key === 'clearance_status'
            ? ''
            : key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
      }

      return {
        accessorKey: key,
        header,
        size: key === 'acknowledgement_status' ? 280 : key === 'clearance_status' ? 200 : 120,
        enableSorting: true,
        enableColumnFilter: true,
        enablePinning: true,
        enableColumnActions: true,
        Cell: ({ cell }) => {
          if (key === 'source_lookup_id') {
            return <span style={{ color: '#24A0ED' }}>{cell.getValue()}</span>;
          }
          if (key === 'acknowledgement_status') {
            const value = cell.getValue();
            if (
              ((isFttsTab && isNewFttsImplementationFlag) ||
                (isIpranTab && isNewIpranImplementationFlag) ||
                (isOtnTab && isNewOtnImplementationFlag)) &&
              value === null
            ) {
              return (
                <div style={{ textAlign: 'center' }}>
                  <span
                    style={{
                      background: '#FFD5D7',
                      color: '#FF6066',
                      padding: '2px 8px',
                      fontSize: '10.5px',
                      fontWeight: '550',
                      borderRadius: '24px',
                    }}
                  >
                    Unacknowledged
                  </span>
                </div>
              );
            }
            return (
              <div style={{ textAlign: 'center' }}>
                <span
                  style={{
                    background: value === 'Unacknowledged' ? '#FFD5D7' : '#8be0c6',
                    color: value === 'Unacknowledged' ? '#FF6066' : '#00B17B',
                    padding: '2px 8px',
                    fontSize: '10.5px',
                    fontWeight: '550',
                    borderRadius: '24px',
                  }}
                >
                  {value}
                </span>
              </div>
            );
          }
          if (key === 'clearance_status') {
            return (
              <div style={{ textAlign: 'center' }}>
                <span
                  style={{
                    background: cell.getValue() === 'Uncleared' ? '#FDE3B1' : '#7FB8FB',
                    color: cell.getValue() === 'Uncleared' ? '#F28231' : '#0087B1',
                    padding: '2px 8px',
                    fontSize: '10.5px',
                    fontWeight: '550',
                    borderRadius: '24px',
                  }}
                >
                  {cell.getValue()}
                </span>
              </div>
            );
          }
          return cell.getValue();
        },
      };
    });

    // Separate status columns from other columns
    const statusColumns = ['acknowledgement_status', 'clearance_status'];
    const filteredKeys = keys?.filter((key) => key !== 'layer_name') || [];

    // Split keys into status columns and regular columns
    const statusKeys = filteredKeys.filter((key) => statusColumns.includes(key));
    const regularKeys = filteredKeys.filter((key) => !statusColumns.includes(key));

    const orderedColumns = [
      ...layerNameColumn,
      ...regularKeys
        .map((key) => columnsWithIndex.find((col) => col.accessorKey === key))
        .filter(Boolean),
      // Add status columns at the end
      ...statusKeys
        .map((key) => columnsWithIndex.find((col) => col.accessorKey === key))
        .filter(Boolean),
    ];

    console.log('Final ordered columns:', orderedColumns);
    return orderedColumns;
  }, [validData, alramType]);
  const table = useMaterialReactTable({
    columns,
    data: validData,
    state: {
      isLoading: alramType === 'history' ? historyLoading : false,
    },
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    initialState: { density: 'compact' },
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30],
      shape: 'rounded',
      variant: 'outlined',
      sx: {
        '& .MuiPaginationItem-root.Mui-selected': {
          backgroundColor: '#BD1D23',
          color: 'white',
        },
      },
    },
    // enableHiding: false,
    enableBottomToolbar: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enablePagination: true,
    muiTableContainerProps: {
      sx: {
        maxHeight: 'calc(100vh - 320px)',
        overflowX: 'auto',
        overflowY: 'auto',
      },
    },
    // muiTableBodyCellProps: {
    // sx: (theme) => ({
    //   backgroundColor:
    //     theme.palette.mode === 'dark'
    //       ? theme.palette.grey[900]
    //       : theme.palette.grey[50],
    // }),
    // },
    muiTableBodyCellProps: {
      sx: {
        color: '#667085',
        backgroundColor: '#fff',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '125%',
        height: '36px',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        color: '#344054',
        backgroundColor: '#E8E9E9',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '125%',
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
      },
    },
    renderDetailPanel: ({ row }) => (
      <>
        <ExpandableTable row={row} alarmType={alramType} />
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <Tooltip title="Download">
        <svg
          style={{ cursor: 'pointer' }}
          onClick={downloadFile}
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
            stroke="#0000008a"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M7 10L12 15L17 10"
            stroke="#0000008a"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M12 15V3"
            stroke="#0000008a"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </Tooltip>
    ),
  });

  const downloadFile = () => {
    // const visibleColumns = table.getVisibleLeafColumns().map(col => col.id).filter(id => id !== 'mrt-row-expand');
    downloadExcelSheetFunction(validData);
  };

  return (
    <div style={{ marginLeft: '32px', marginRight: '32px', marginTop: '20px' }}>
      <div style={{ width: '-webkit-fill-available' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            height: '44px',
          }}
        >
          <div className="flex">
            <a
              id="switch_on"
              className={`toggleStyle ${alramType === 'live' ? 'selected' : 'not_selected'}`}
              onClick={() => handleChange('live')}
            >
              Live Alarms
            </a>
            <a
              id="switch_off"
              className={`toggleStyle ${alramType === 'history' ? 'selected' : 'not_selected'}`}
              onClick={() => handleChange('history')}
            >
              History Alarms
            </a>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <div style={{ background: '#F8E8E9' }}>
              {alramType === 'history' && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateRangePickerComponent
                    selectedDate={selectedDate}
                    handleDateChange={handleDateChange}
                  />
                  {/* <DatePicker
                      name="birthDate"
                      value={selectedDate}
                      onChange={handleDateChange}
                      sx={{ height: 44, '& .MuiInputBase-root': { height: 44 } }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          size="small"
                        />
                      )}
                    /> */}
                </LocalizationProvider>
              )}
            </div>
            <div className="flex-table">
              <a
                className={state.right ? 'table-icons' : 'table-icon'}
                onClick={toggleDrawer('right', true)}
              >
                <Tooltip title="Filter">
                  <TuneIcon />
                </Tooltip>
              </a>
            </div>
            <div className="flex-table">
              <a
                className={viewType === 'graph' ? 'table-icons' : 'table-icon-single'}
                onClick={() => setViewType('graph')}
              >
                <Tooltip title="Map">
                  <MapIcon />
                </Tooltip>
              </a>
              <a
                className={viewType === 'table' ? 'table-icons' : 'table-icon-single'}
                onClick={() => setViewType('table')}
              >
                <Tooltip title="Table">
                  <TableChartIcon />
                </Tooltip>
              </a>
            </div>
          </div>
        </Box>
      </div>
      <div className="box" style={{ marginTop: '20px' }}>
        <MaterialReactTable table={table} />
      </div>
    </div>
  );
}
